import React, { useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  getSleepCollection,
  getSleepCollectionProducts,
  getSleepCollectionFetchingState,
  getSleepGoalSubCollections,
} from "../../utils/reduxSelectors";
import {
  fetchCollectionRequest,
  fetchSleepGoalSubCollectionsRequest,
} from "../../actions/ShopActions";

// Constants
import {
  COLLECTION_HANDLES,
  SLEEP_COLLECTION_KEY,
} from "../../utils/collectionConstants";

let hasFetchedSleepCollection = false;
let hasFetchedSleepGoalCollections = false;

/*
  Custom hook to pull in sleep product collection data from Shopify.
  Use this in functional components.
  Reference:
  https://blog.logrocket.com/how-to-convert-your-existing-redux-containers-to-hooks/
*/
export const useSleepProductCollection = () => {
  const sleepCollectionProducts = useSelector(
    (state) => getSleepCollectionProducts(state),
    shallowEqual
  );
  const sleepCollectionMetadata = useSelector(
    (state) => getSleepCollection(state).metadata,
    shallowEqual
  );
  const sleepGoalCollections = useSelector(
    (state) => getSleepGoalSubCollections(state),
    shallowEqual
  );
  const isFetching = useSelector((state) =>
    getSleepCollectionFetchingState(state)
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isFetching && !hasFetchedSleepCollection) {
      dispatch(
        fetchCollectionRequest(COLLECTION_HANDLES[SLEEP_COLLECTION_KEY])
      );
      hasFetchedSleepCollection = true;
    }
  }, [dispatch, isFetching]);

  useEffect(() => {
    if (!isFetching && !hasFetchedSleepGoalCollections) {
      dispatch(fetchSleepGoalSubCollectionsRequest());
      hasFetchedSleepGoalCollections = true;
    }
  }, [dispatch, isFetching]);

  return {
    sleepCollectionProducts,
    sleepCollectionMetadata,
    sleepGoalCollections,
    isFetching,
  };
};

/*
  Container/wrapper component that uses custom hook to pull in sleep product
  collection data from Shopify.
  Use this in class components.
*/
const withSleepProductCollection = (WrappedComponent) => {
  return (props) => {
    const {
      sleepCollectionProducts,
      sleepCollectionMetadata,
      sleepGoalCollections,
      isFetching,
    } = useSleepProductCollection();

    return (
      <WrappedComponent
        productData={sleepCollectionProducts}
        collectionMetadata={sleepCollectionMetadata}
        sleepGoalCollections={sleepGoalCollections}
        isFetching={isFetching}
        {...props}
      />
    );
  };
};

export default withSleepProductCollection;
